import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useContentNode } from '@/hooks/useContentNode';
import { ContentNodeResponse, ContentNodeTranslation } from '@/types';

function pickTranslation(node: ContentNodeResponse, locale: string): ContentNodeTranslation | null {
  return (
    node.translations.find(t => t.locale === locale && t.published) ??
    node.translations.find(t => t.published) ??
    null
  );
}

const CmsPage: React.FC = () => {
  const { slug = '', childSlug } = useParams<{ slug: string; childSlug?: string }>();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { node, translation, loading, error } = useContentNode(slug);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl space-y-8">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !node || !translation) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl text-center text-muted-foreground">
        {error ?? t('common.notFound', 'Content not found')}
      </div>
    );
  }

  const locale = i18n.language;
  const children = [...node.children].sort((a, b) => a.sortOrder - b.sortOrder);

  // 有子節點但沒有 childSlug → redirect 到第一個子節點
  if (children.length > 0 && !childSlug) {
    return <Navigate to={`/resources/${slug}/${children[0].slug}`} replace />;
  }

  // 無子節點，直接顯示內容
  if (children.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-3">{translation.title}</h1>
          {translation.subtitle && <p className="text-muted-foreground">{translation.subtitle}</p>}
        </div>
        {translation.body && (
          <>
            <Separator />
            <div
              className="prose prose-sm max-w-none text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: translation.body }}
            />
          </>
        )}
      </div>
    );
  }

  // 有子節點，Tab 各自獨立 URL
  const activeChild = children.find(c => c.slug === childSlug) ?? children[0];
  const activeCt = pickTranslation(activeChild, locale);

  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-3">{translation.title}</h1>
        {translation.subtitle && <p className="text-muted-foreground">{translation.subtitle}</p>}
      </div>

      <Tabs value={activeChild.slug} onValueChange={val => navigate(`/resources/${slug}/${val}`)}>
        <TabsList className="flex-wrap h-auto gap-1">
          {children.map(child => {
            const ct = pickTranslation(child, locale);
            return (
              <TabsTrigger key={child.slug} value={child.slug}>
                {ct?.title ?? child.slug}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {children.map(child => {
          const ct = pickTranslation(child, locale);
          return (
            <TabsContent key={child.slug} value={child.slug} className="mt-6 space-y-4">
              {ct?.subtitle && <p className="text-sm text-muted-foreground">{ct.subtitle}</p>}
              <Separator />
              {ct?.body ? (
                <div
                  className="prose prose-sm max-w-none text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: ct.body }}
                />
              ) : (
                <p className="text-sm text-muted-foreground">{t('common.noContent')}</p>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      {/* SEO: 讓搜尋機器人看到當前 Tab 的標題和描述 */}
      {activeCt && (
        <div className="sr-only" aria-hidden="false">
          <h2>{activeCt.title}</h2>
          {activeCt.subtitle && <p>{activeCt.subtitle}</p>}
        </div>
      )}
    </div>
  );
};

export default CmsPage;
